import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  DesktopWindows as DesktopWindowsIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  PhoneIphone as PhoneIphoneIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  TabletMac as TabletMacIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppMode } from '../contexts/AppModeContext';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';
import { AdminKeyService } from '../services/adminKeyService';
import Footer from '../components/Layout/Footer';
import MediaPickerModal from '../components/MediaPickerModal';
import type { MediaItem } from '../lib/media';

type FooterLink = {
  label: string;
  href: string;
  newTab?: boolean;
};

type FooterColumn = {
  title: string;
  links: FooterLink[];
};

type FooterSocial = {
  platform: 'facebook' | 'twitter' | 'instagram' | 'youtube' | 'tiktok' | 'linkedin' | 'github' | 'custom';
  href: string;
  label?: string;
};

type FooterCompany = {
  blurb?: string;
  email?: string;
  phone?: string;
  address?: string;
  logoPath?: string;
  wordmarkPath?: string;
};

type FooterConfig = {
  enabled?: boolean;
  showOnHomepage?: boolean;
  company?: FooterCompany;
  columns?: FooterColumn[];
  social?: FooterSocial[];
  legalLinks?: FooterLink[];
  copyrightText?: string;
};

const defaultFooterConfig: FooterConfig = {
  enabled: true,
  showOnHomepage: true,
  company: {
    blurb: 'Dein ultimatives Ziel für Mode. Entdecke großartige Kleidung von Top-Marken und unabhängigen Verkäufern. Kaufe und verkaufe mit Vertrauen.',
    email: 'support@example.com',
    phone: '+49 0000 000000',
    address: 'Musterstraße 1, 10115 Berlin',
    logoPath: '/sistasouk.png',
    wordmarkPath: '/sistasouk_font.png',
  },
  columns: [
    {
      title: 'Shop',
      links: [
        { label: 'Damen', href: '/products?category=women' },
        { label: 'Herren', href: '/products?category=men' },
        { label: 'Kinder', href: '/products?category=kids' },
        { label: 'Accessoires', href: '/products?category=accessories' },
        { label: 'Sale', href: '/products?sale=true' },
      ],
    },
    {
      title: 'Kundenservice',
      links: [
        { label: 'Kontakt', href: '/contact' },
        { label: 'Größentabelle', href: '/size-guide' },
        { label: 'Versandinformationen', href: '/shipping' },
        { label: 'Rücksendungen', href: '/returns' },
        { label: 'FAQ', href: '/faq' },
      ],
    },
    {
      title: 'Unternehmen',
      links: [
        { label: 'Über uns', href: '/about' },
        { label: 'Karriere', href: '/careers' },
        { label: 'Presse', href: '/press' },
        { label: 'Nachhaltigkeit', href: '/sustainability' },
        { label: 'Investor Relations', href: '/investors' },
      ],
    },
  ],
  social: [
    { platform: 'facebook', href: 'https://facebook.com' },
    { platform: 'twitter', href: 'https://twitter.com' },
    { platform: 'instagram', href: 'https://instagram.com' },
    { platform: 'youtube', href: 'https://youtube.com' },
  ],
  legalLinks: [
    { label: 'Datenschutz', href: '/datenschutz' },
    { label: 'Nutzungsbedingungen', href: '/agb' },
    { label: 'Cookie-Richtlinie', href: '/cookies' },
    { label: 'Barrierefreiheit', href: '/accessibility' },
    { label: 'Plattform-Regeln', href: '/regeln' },
  ],
};

const ComponentEditor: React.FC = () => {
  const navigate = useNavigate();
  const { mode } = useAppMode();
  const { settings } = useWebsiteSettings();

  const existing: FooterConfig | null = React.useMemo(() => {
    const f = (settings as any)?.footer;
    if (f && typeof f === 'object') return f as FooterConfig;
    return null;
  }, [settings]);

  const initial = React.useMemo(() => {
    const base = existing || defaultFooterConfig;
    return JSON.parse(JSON.stringify(base)) as FooterConfig;
  }, [existing]);

  const [draft, setDraft] = React.useState<FooterConfig>(initial);
  const [saving, setSaving] = React.useState(false);
  const [previewMode, setPreviewMode] = React.useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [mediaPicker, setMediaPicker] = React.useState<{ open: boolean; target: 'logo' | 'wordmark' | null }>({ open: false, target: null });

  React.useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const dirty = React.useMemo(() => {
    try {
      return JSON.stringify(draft) !== JSON.stringify(initial);
    } catch {
      return true;
    }
  }, [draft, initial]);

  const previewWidth = React.useMemo(() => {
    if (previewMode === 'mobile') return 390;
    if (previewMode === 'tablet') return 820;
    return '100%';
  }, [previewMode]);

  const isDesktopPreview = previewMode === 'desktop';

  const toPublicUrl = React.useCallback((p?: string | null) => {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    try {
      const port = window.location.port;
      const isDevDifferentOrigin = port && port !== '8000';
      if (isDevDifferentOrigin && p.startsWith('/')) {
        const base = `${window.location.protocol}//${window.location.hostname}:8000`;
        return base + p;
      }
    } catch {
      // ignore
    }
    return p;
  }, []);

  const normalizePickedUrl = React.useCallback((url: string) => {
    const u = String(url || '');
    if (!u) return '';
    // Prefer storing /uploads/... paths when possible, to keep settings portable.
    const i = u.indexOf('/uploads/');
    if (i >= 0) return u.slice(i);
    return u;
  }, []);

  const openMediaPicker = (target: 'logo' | 'wordmark') => {
    setMediaPicker({ open: true, target });
  };

  const closeMediaPicker = () => {
    setMediaPicker({ open: false, target: null });
  };

  const onPickMedia = (item: MediaItem) => {
    const picked = normalizePickedUrl(item?.url || '');
    if (!picked) {
      closeMediaPicker();
      return;
    }
    if (mediaPicker.target === 'logo') updateCompany({ logoPath: picked });
    if (mediaPicker.target === 'wordmark') updateCompany({ wordmarkPath: picked });
    closeMediaPicker();
  };

  const updateDraft = (patch: Partial<FooterConfig>) => {
    setDraft((p) => ({ ...(p || {}), ...(patch || {}) }));
  };

  const updateCompany = (patch: Partial<FooterCompany>) => {
    setDraft((p) => ({
      ...(p || {}),
      company: { ...((p || {}).company || {}), ...(patch || {}) },
    }));
  };

  const save = async () => {
    if (saving) return;
    let token = await AdminKeyService.getSecret();
    if (!token) {
      toast.error('Missing admin key.');
      return;
    }

    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';

    const loadingId = toast.loading('Saving footer...');
    setSaving(true);
    try {
      const payload: any = {
        mode,
        footer: draft,
        token,
      };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      try {
        window.dispatchEvent(new CustomEvent('websiteSettingsUpdated', { detail: { footer: draft } }));
      } catch {}
      toast.success('Footer saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save footer: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSaving(false);
    }
  };

  const addColumn = () => {
    setDraft((p) => ({
      ...(p || {}),
      columns: [...(Array.isArray(p?.columns) ? p!.columns! : []), { title: 'Neue Spalte', links: [] }],
    }));
  };

  const updateColumn = (idx: number, patch: Partial<FooterColumn>) => {
    setDraft((p) => {
      const cols = Array.isArray(p?.columns) ? [...p!.columns!] : [];
      const current = cols[idx] || { title: '', links: [] };
      cols[idx] = { ...current, ...patch };
      return { ...(p || {}), columns: cols };
    });
  };

  const removeColumn = (idx: number) => {
    setDraft((p) => {
      const cols = Array.isArray(p?.columns) ? [...p!.columns!] : [];
      cols.splice(idx, 1);
      return { ...(p || {}), columns: cols };
    });
  };

  const addLinkToColumn = (colIdx: number) => {
    setDraft((p) => {
      const cols = Array.isArray(p?.columns) ? [...p!.columns!] : [];
      const col = cols[colIdx] || { title: '', links: [] };
      const links = Array.isArray(col.links) ? [...col.links, { label: 'Neuer Link', href: '/' }] : [{ label: 'Neuer Link', href: '/' }];
      cols[colIdx] = { ...col, links };
      return { ...(p || {}), columns: cols };
    });
  };

  const updateColumnLink = (colIdx: number, linkIdx: number, patch: Partial<FooterLink>) => {
    setDraft((p) => {
      const cols = Array.isArray(p?.columns) ? [...p!.columns!] : [];
      const col = cols[colIdx] || { title: '', links: [] };
      const links = Array.isArray(col.links) ? [...col.links] : [];
      const cur = links[linkIdx] || { label: '', href: '' };
      links[linkIdx] = { ...cur, ...patch };
      cols[colIdx] = { ...col, links };
      return { ...(p || {}), columns: cols };
    });
  };

  const removeColumnLink = (colIdx: number, linkIdx: number) => {
    setDraft((p) => {
      const cols = Array.isArray(p?.columns) ? [...p!.columns!] : [];
      const col = cols[colIdx] || { title: '', links: [] };
      const links = Array.isArray(col.links) ? [...col.links] : [];
      links.splice(linkIdx, 1);
      cols[colIdx] = { ...col, links };
      return { ...(p || {}), columns: cols };
    });
  };

  const addSocial = () => {
    setDraft((p) => ({
      ...(p || {}),
      social: [...(Array.isArray(p?.social) ? p!.social! : []), { platform: 'custom', href: '' }],
    }));
  };

  const updateSocial = (idx: number, patch: Partial<FooterSocial>) => {
    setDraft((p) => {
      const next = Array.isArray(p?.social) ? [...p!.social!] : [];
      const cur = next[idx] || { platform: 'custom', href: '' };
      next[idx] = { ...cur, ...patch } as any;
      return { ...(p || {}), social: next };
    });
  };

  const removeSocial = (idx: number) => {
    setDraft((p) => {
      const next = Array.isArray(p?.social) ? [...p!.social!] : [];
      next.splice(idx, 1);
      return { ...(p || {}), social: next };
    });
  };

  const addLegal = () => {
    setDraft((p) => ({
      ...(p || {}),
      legalLinks: [...(Array.isArray(p?.legalLinks) ? p!.legalLinks! : []), { label: 'Neuer Link', href: '/' }],
    }));
  };

  const updateLegal = (idx: number, patch: Partial<FooterLink>) => {
    setDraft((p) => {
      const next = Array.isArray(p?.legalLinks) ? [...p!.legalLinks!] : [];
      const cur = next[idx] || { label: '', href: '' };
      next[idx] = { ...cur, ...patch };
      return { ...(p || {}), legalLinks: next };
    });
  };

  const removeLegal = (idx: number) => {
    setDraft((p) => {
      const next = Array.isArray(p?.legalLinks) ? [...p!.legalLinks!] : [];
      next.splice(idx, 1);
      return { ...(p || {}), legalLinks: next };
    });
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <IconButton onClick={() => navigate('/admin')} aria-label="Back to Admin" sx={{ border: '1px solid', borderColor: 'divider' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.1 }}>Component Editor</Typography>
              <Typography variant="body2" color="text.secondary">Footer builder: columns, links, socials, contact, visibility</Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.25} alignItems="center" justifyContent={{ xs: 'space-between', md: 'flex-end' }}>
            {dirty ? <Chip label="Unsaved" color="warning" variant="outlined" /> : <Chip label="Saved" color="success" variant="outlined" />}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!dirty || saving}
              onClick={save}
              sx={{ borderRadius: 999, px: 2.25, fontWeight: 900 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>

        <Stack direction={isDesktopPreview ? 'column' : ({ xs: 'column', lg: 'row' } as any)} spacing={2.5} alignItems="flex-start">
          {isDesktopPreview && (
            <Box sx={{ width: '100%' }}>
              <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 950 }}>Live preview</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={previewMode}
                      onChange={(_, v) => { if (v) setPreviewMode(v); }}
                      sx={{
                        borderRadius: 999,
                        bgcolor: 'background.paper',
                        '& .MuiToggleButton-root': {
                          border: '1px solid',
                          borderColor: 'divider',
                          px: 1.2,
                          py: 0.6,
                          borderRadius: 999,
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { bgcolor: 'primary.dark' },
                        },
                      }}
                    >
                      <ToggleButton value="desktop" aria-label="Desktop">
                        <DesktopWindowsIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton value="tablet" aria-label="Tablet">
                        <TabletMacIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton value="mobile" aria-label="Mobile">
                        <PhoneIphoneIcon fontSize="small" />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Preview uses your current draft (not saved yet).
                  </Typography>
                </CardContent>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ bgcolor: 'grey.50', p: { xs: 1.5, sm: 2 }, display: 'flex', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 1180,
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 16px 40px rgba(2,6,23,0.12)',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Footer configOverride={draft} forceShow={true} />
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Box>
          )}

          <Box sx={{ flex: '1 1 620px', width: '100%' }}>
            <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography sx={{ fontWeight: 950, mb: 1 }}>Visibility</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={<Switch checked={draft.enabled !== false} onChange={(e) => updateDraft({ enabled: e.target.checked })} />}
                    label="Footer enabled"
                  />
                  <FormControlLabel
                    control={<Switch checked={draft.showOnHomepage !== false} onChange={(e) => updateDraft({ showOnHomepage: e.target.checked })} />}
                    label="Show on homepage"
                  />
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <Typography sx={{ fontWeight: 950, mb: 1 }}>Company</Typography>
                <Stack spacing={1.5}>
                  <TextField label="Blurb" value={draft.company?.blurb || ''} onChange={(e) => updateCompany({ blurb: e.target.value })} multiline minRows={3} />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField label="Support email" value={draft.company?.email || ''} onChange={(e) => updateCompany({ email: e.target.value })} fullWidth />
                    <TextField label="Phone" value={draft.company?.phone || ''} onChange={(e) => updateCompany({ phone: e.target.value })} fullWidth />
                  </Stack>
                  <TextField label="Address" value={draft.company?.address || ''} onChange={(e) => updateCompany({ address: e.target.value })} />

                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          flex: '0 0 auto',
                        }}
                      >
                        {draft.company?.logoPath ? (
                          <Box component="img" src={toPublicUrl(draft.company.logoPath)} alt="Logo" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <ImageIcon color="disabled" />
                        )}
                      </Box>
                      <TextField
                        label="Logo (icon)"
                        value={draft.company?.logoPath || ''}
                        onChange={(e) => updateCompany({ logoPath: e.target.value })}
                        fullWidth
                        placeholder="Choose from Media Gallery"
                      />
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => openMediaPicker('logo')} startIcon={<ImageIcon />} sx={{ borderRadius: 999, fontWeight: 900 }}>
                          Gallery
                        </Button>
                        <IconButton
                          onClick={() => updateCompany({ logoPath: '' })}
                          aria-label="Clear logo"
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          flex: '0 0 auto',
                        }}
                      >
                        {draft.company?.wordmarkPath ? (
                          <Box component="img" src={toPublicUrl(draft.company.wordmarkPath)} alt="Wordmark" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <ImageIcon color="disabled" />
                        )}
                      </Box>
                      <TextField
                        label="Logo (wordmark)"
                        value={draft.company?.wordmarkPath || ''}
                        onChange={(e) => updateCompany({ wordmarkPath: e.target.value })}
                        fullWidth
                        placeholder="Choose from Media Gallery"
                      />
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => openMediaPicker('wordmark')} startIcon={<ImageIcon />} sx={{ borderRadius: 999, fontWeight: 900 }}>
                          Gallery
                        </Button>
                        <IconButton
                          onClick={() => updateCompany({ wordmarkPath: '' })}
                          aria-label="Clear wordmark"
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Button
                      variant="text"
                      onClick={() => {
                        // Re-apply to refresh preview URLs in dev if needed
                        updateCompany({
                          logoPath: draft.company?.logoPath || '',
                          wordmarkPath: draft.company?.wordmarkPath || '',
                        });
                      }}
                      startIcon={<RefreshIcon />}
                      sx={{ alignSelf: 'flex-start', borderRadius: 999, fontWeight: 900 }}
                    >
                      Refresh preview
                    </Button>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 950 }}>Columns</Typography>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addColumn} sx={{ borderRadius: 999, fontWeight: 900 }}>
                    Add column
                  </Button>
                </Stack>

                <Stack spacing={1.25}>
                  {(Array.isArray(draft.columns) ? draft.columns : []).map((col, colIdx) => (
                    <Accordion key={colIdx} defaultExpanded sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
                      >
                        <Typography sx={{ fontWeight: 900, flexGrow: 1 }} noWrap>
                          {col.title || `Column ${colIdx + 1}`}
                        </Typography>
                        <IconButton onClick={(e) => { e.stopPropagation(); e.preventDefault(); removeColumn(colIdx); }} size="small" aria-label="Remove column">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1.25}>
                          <TextField label="Column title" value={col.title || ''} onChange={(e) => updateColumn(colIdx, { title: e.target.value })} />

                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography sx={{ fontWeight: 900 }}>Links</Typography>
                            <Button variant="text" size="small" startIcon={<AddIcon />} onClick={() => addLinkToColumn(colIdx)} sx={{ borderRadius: 999, fontWeight: 900 }}>
                              Add link
                            </Button>
                          </Stack>

                          <Stack spacing={1}>
                            {(Array.isArray(col.links) ? col.links : []).map((lnk, linkIdx) => (
                              <Card key={linkIdx} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <TextField label="Label" value={lnk.label || ''} onChange={(e) => updateColumnLink(colIdx, linkIdx, { label: e.target.value })} fullWidth />
                                    <TextField label="URL" value={lnk.href || ''} onChange={(e) => updateColumnLink(colIdx, linkIdx, { href: e.target.value })} fullWidth />
                                    <FormControlLabel
                                      control={<Switch checked={lnk.newTab === true} onChange={(e) => updateColumnLink(colIdx, linkIdx, { newTab: e.target.checked })} />}
                                      label="New tab"
                                      sx={{ ml: 0 }}
                                    />
                                    <IconButton onClick={() => removeColumnLink(colIdx, linkIdx)} aria-label="Remove link">
                                      <DeleteIcon />
                                    </IconButton>
                                  </Stack>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 950 }}>Social</Typography>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addSocial} sx={{ borderRadius: 999, fontWeight: 900 }}>
                    Add
                  </Button>
                </Stack>

                <Stack spacing={1}>
                  {(Array.isArray(draft.social) ? draft.social : []).map((s, idx) => (
                    <Card key={idx} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <Select
                            value={s.platform || 'custom'}
                            onChange={(e) => updateSocial(idx, { platform: e.target.value as any })}
                            size="small"
                            sx={{ minWidth: 160, borderRadius: 2 }}
                          >
                            {['facebook','twitter','instagram','youtube','tiktok','linkedin','github','custom'].map((p) => (
                              <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                          </Select>
                          <TextField label="URL" value={s.href || ''} onChange={(e) => updateSocial(idx, { href: e.target.value })} fullWidth />
                          <IconButton onClick={() => removeSocial(idx)} aria-label="Remove social">
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 950 }}>Legal links</Typography>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addLegal} sx={{ borderRadius: 999, fontWeight: 900 }}>
                    Add
                  </Button>
                </Stack>

                <Stack spacing={1}>
                  {(Array.isArray(draft.legalLinks) ? draft.legalLinks : []).map((l, idx) => (
                    <Card key={idx} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField label="Label" value={l.label || ''} onChange={(e) => updateLegal(idx, { label: e.target.value })} fullWidth />
                          <TextField label="URL" value={l.href || ''} onChange={(e) => updateLegal(idx, { href: e.target.value })} fullWidth />
                          <IconButton onClick={() => removeLegal(idx)} aria-label="Remove legal">
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <TextField
                  label="Copyright text (optional)"
                  value={draft.copyrightText || ''}
                  onChange={(e) => updateDraft({ copyrightText: e.target.value })}
                  placeholder="© 2026 Your Brand. Alle Rechte vorbehalten."
                />
              </CardContent>
            </Card>
          </Box>

          {!isDesktopPreview && (
          <Box sx={{ flex: '0 0 420px', width: '100%' }}>
            <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 950 }}>Live preview</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={previewMode}
                    onChange={(_, v) => { if (v) setPreviewMode(v); }}
                    sx={{
                      borderRadius: 999,
                      bgcolor: 'background.paper',
                      '& .MuiToggleButton-root': {
                        border: '1px solid',
                        borderColor: 'divider',
                        px: 1.2,
                        py: 0.6,
                        borderRadius: 999,
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    <ToggleButton value="desktop" aria-label="Desktop">
                      <DesktopWindowsIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="tablet" aria-label="Tablet">
                      <TabletMacIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="mobile" aria-label="Mobile">
                      <PhoneIphoneIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Preview uses your current draft (not saved yet).
                </Typography>
              </CardContent>
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    overflowX: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      width: previewWidth,
                      minWidth: previewMode === 'tablet' ? 600 : 320,
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 16px 40px rgba(2,6,23,0.12)',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Footer configOverride={draft} forceShow={true} />
                  </Box>
                </Box>
              </Box>
            </Card>
          </Box>
          )}
        </Stack>
      </Container>

      <MediaPickerModal
        open={mediaPicker.open}
        onClose={closeMediaPicker}
        onSelect={onPickMedia}
      />
    </Box>
  );
};

export default ComponentEditor;
